import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ShapeElement } from "./ShapeElement";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { Tweens, ITweenable } from "../../Effects/Tweens";
import { IElement } from "../Interfaces/IElement";

/**
 * BaseElementParticle class is class which creates holder for other display objects.
 * Also handle all basic properties of display object as X, Y, Rotation, Tween, Scale, Visible, Mask.
 * @class BaseElementParticle
 * @extends PIXI.Container
 */
@engineComponent(ComponentTypes.DisplayObject)
export class BaseElementParticle extends PIXI.particles.ParticleContainer implements ITweenable, IElement {
    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    maskSprite: PIXI.Sprite;

    constructor() {
        super();

        this._cloned = false;
        this.__destroyed = false;

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @method _create - setup element from xml node
     * @param node
     * @private
     */
    _create(node: Element): void {
        this.assertDestroyed("_create()");

        Tweens.removeTweens(this);

        if (this.name == null) {
            this.name = node.nodeName;
        }

        /*
         * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
         * @property Alpha
         * @type Number
         * @default 1
         */
        if (node.hasAttribute("Alpha")) {
            this.alpha = parseFloat(node.getAttribute("Alpha"));
        }

        /*
         * Rotation in degrees for this display object.
         * @property Rotation
         * @type Number
         * @default 0
         */
        if (node.hasAttribute("Rotation")) {
            this.rotation = parseInt(node.getAttribute("Rotation"));
        }

        /*
         * Random rotation in degrees for this display object.
         * @property StartRotation
         * @type Number
         * @default null
         */
        if (node.hasAttribute("StartRotation")) {
            if (node.getAttribute("StartRotation") === "random") {
                //this.rotation = Math.floor(Math.random() * 360);
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        /*
         * The factor to stretch this display object horizontally.
         * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
         * @property ScaleX
         * @type Number
         * @default 1
         */
        if (node.hasAttribute("ScaleX")) {
            this.scale.x = parseFloat(node.getAttribute("scaleX"));
        }

        /*
         * The factor to stretch this display object vertically.
         * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
         * @property ScaleY
         * @type Number
         * @default 1
         */
        if (node.hasAttribute("ScaleY")) {
            this.scale.y = parseFloat(node.getAttribute("ScaleY"));
        }

        /*
         * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
         * @property Scale
         * @type Number
         * @default 1
         */
        if (node.hasAttribute("Scale")) {
            this.scale.x = parseFloat(node.getAttribute("Scale"));
            this.scale.y = parseFloat(node.getAttribute("Scale"));
        }

        /*
         * X (horizontal) position of the display object, relative to its parent.
         * @property X
         * @type Number
         * @default 0
         */
        if (node.hasAttribute("X")) {
            this.position.x = parseInt(node.getAttribute("X"));
        }

        /*
         * Y (vertical) position of the display object, relative to its parent.
         * @property Y
         * @type Number
         * @default 0
         */
        if (node.hasAttribute("Y")) {
            this.position.y = parseInt(node.getAttribute("Y"));
        }

        /*
         * X pivot for container. Used mainly for scale tweening and rotation.
         * @property PivotX
         * @type number
         * @default null
         */
        if (node.hasAttribute("PivotX")) {
            this.pivot.x = parseInt(node.getAttribute("PivotX"));
        }

        /*
         * Y pivot for container. Used mainly for scale tweening and rotation.
         * @property PivotY
         * @type number
         * @default null
         */
        if (node.hasAttribute("PivotY")) {
            this.pivot.y = parseInt(node.getAttribute("PivotY"));
        }

        /*
         * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
         * @property Visible
         * @type Boolean
         * @default true
         */
        if (node.hasAttribute("Visible")) {
            this.visible = StringUtils.parseBoolean(node.getAttribute("Visible"));
        }

        /*
         * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
         * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
         * @property Mask
         * @type ShapeElement
         * @default null
         */
        if (node.hasAttribute("Mask")) {
            var next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute("Mask")) {
                    var shapeElement: ShapeElement = new ShapeElement();
                    shapeElement.create(next);
                    this.mask = shapeElement;
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute("Mask"));
                }
            }
        }

        /*
         * A ImageElement instance that defines a mask for this display object.
         * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
         * @property BitmapMask
         * @type string
         * @default null
         */
        if (node.hasAttribute("BitmapMask")) {
            var image = StageController.getTextureByResourceId(node.getAttribute("BitmapMask"));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute("BitmapMaskX")) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute("BitmapMaskX"));
                }
                if (node.hasAttribute("BitmapMaskY")) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute("BitmapMaskY"));
                }
                if (!node.hasAttribute("BitmapMaskShow")) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no image with name for mask: " + node.getAttribute("BitmapMask"));
                }
            }
        }

        this._positionTweenOnCreate(node);
        this._scaleTweenOnCreate(node);
        this._rotationTweenOnCreate(node);
        this._alphaTweenOnCreate(node);

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * Get scale-tweening-specific attributes from xml and fire the tween
     * @method _scaleTweenOnCreate
     * @param node is Element from xml
     * @return void
     */
    private _positionTweenOnCreate(node: Element): void {
        // todo: PositionTween attribute, which takes either one value that applies both for X and Y axis, or pairs of values in format "(x1,y1);(x2,x2);(x3,x4)"

        if (
            !node.hasAttribute(BaseElementParticleAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(BaseElementParticleAttributes.POSITION_TWEEN_Y)
        ) {
            return;
        }

        let tweenX: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(BaseElementParticleAttributes.POSITION_TWEEN_X),
        );
        let tweenY: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(BaseElementParticleAttributes.POSITION_TWEEN_Y),
        );

        if (tweenX == null || tweenY == null) {
            if (DEBUG) {
                this.log("WARNING wrong parameters for PositionTween");
            }

            return;
        }

        const tweenDuration: number = node.hasAttribute(BaseElementParticleAttributes.POSITION_TWEEN_DURATION)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.POSITION_TWEEN_DURATION))
            : 120;
        const tweenEase: string = node.hasAttribute(BaseElementParticleAttributes.POSITION_TWEEN_EASE)
            ? node.getAttribute(BaseElementParticleAttributes.POSITION_TWEEN_EASE)
            : null;
        const position: string = node.hasAttribute(BaseElementParticleAttributes.POSITION_TWEEN_POSITION)
            ? node.getAttribute(BaseElementParticleAttributes.POSITION_TWEEN_POSITION)
            : null;

        if (position == BaseElementParticleAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            tweenX = tweenX.map((value) => {
                return value + this.position.x;
            });

            tweenY = tweenY.map((value) => {
                return value + this.position.y;
            });
        }

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration });
    }

    /**
     * Get scale-tweening-specific attributes from xml and fire the tween
     * @method _scaleTweenOnCreate
     * @param node is Element from xml
     * @return void
     */
    private _scaleTweenOnCreate(node: Element): void {
        if (!node.hasAttribute(BaseElementParticleAttributes.SCALE_TWEEN)) {
            return;
        }

        const tweenValues: Array<PIXI.Point> = BaseElementParticle._getPointsFromPointsAttribute(
            node.getAttribute(BaseElementParticleAttributes.SCALE_TWEEN),
        );
        const tweenDuration: number = node.hasAttribute(BaseElementParticleAttributes.SCALE_TWEEN_DURATION)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.SCALE_TWEEN_DURATION))
            : 120;
        const tweenEase: string = node.hasAttribute(BaseElementParticleAttributes.SCALE_TWEEN_EASE)
            ? node.getAttribute(BaseElementParticleAttributes.SCALE_TWEEN_EASE)
            : null;

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration });
    }

    /**
     * Get rotation-tweening-specific attributes from xml and fire the tween
     * @method _scaleTweenOnCreate
     * @param node is Element from xml
     * @return void
     */
    private _rotationTweenOnCreate(node: Element): void {
        if (!node.hasAttribute(BaseElementParticleAttributes.ROTATION_TWEEN)) {
            return;
        }

        const degrees: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(BaseElementParticleAttributes.ROTATION_TWEEN),
        );

        if (degrees == null) {
            if (DEBUG) {
                this.log("WARNING wrong parameters for RotationTween");
            }

            return;
        }

        const tweenDuration: number = node.hasAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_DURATION)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_DURATION))
            : 120;
        const tweenEase: string = node.hasAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_EASE)
            ? node.getAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_EASE)
            : null;
        const tweenDelay: number = node.hasAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_DELAY)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.ROTATION_TWEEN_DELAY))
            : 0;

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay });
    }

    /**
     * Get alpha-tweening-specific attributes from xml and fire the tween
     * @method _scaleTweenOnCreate
     * @param node is Element from xml
     * @return void
     */
    private _alphaTweenOnCreate(node: Element): void {
        if (!node.hasAttribute(BaseElementParticleAttributes.ALPHA_TWEEN)) {
            return;
        }

        const alphas: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(BaseElementParticleAttributes.ALPHA_TWEEN),
        );

        if (alphas == null) {
            if (DEBUG) {
                this.log("WARNING wrong parameters for RotationTween");
            }
            return;
        }

        const tweenDuration: number = node.hasAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_DURATION)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_DURATION))
            : 120;
        const tweenDelay: number = node.hasAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_DELAY)
            ? parseInt(node.getAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_DELAY))
            : 0;
        const tweenEase: string = node.hasAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_EASE)
            ? node.getAttribute(BaseElementParticleAttributes.ALPHA_TWEEN_EASE)
            : null;

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay });
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    private static _getPointsFromPointsAttribute(pointsString: string): any {
        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) => {
                return /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair];
            })
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        this._create(node);
    }

    getDebugInfo(): string {
        if (DEBUG) {
            var debugInfoFn = (this as any).debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = BaseElementParticle._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy(options);
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    /**
     * @method internalDestroy - cleanup resources
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    public clone(): any {
        throw new Error("Not implemented");
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    _removeAndDestroyChildren(): void {
        this.assertDestroyed("_removeAndDestroyChildren");

        var destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (var i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * Marks element as cloned
     */
    protected markAsCloned(): void {
        this._markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    _markAsCloned(): void {
        this.assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param message
     */
    protected logError(s: string): void {
        if (DEBUG) {
            var compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }
}

const BaseElementParticleAttributes = {
    /*
     * Blend mode for animation.
     * Bleding modes:
     *   0 = NORMAL;
     *   1 = ADD;
     *   2 = MULTIPLY;
     *   3 = SCREEN;
     *   4 = OVERLAY;
     *   5 = DARKEN;
     *   6 = LIGHTEN;
     *   7 = COLOR_DODGE;
     *   8 = COLOR_BURN;
     *   9 = HARD_LIGHT;
     *   10 = SOFT_LIGHT;
     *   11 = DIFFERENCE;
     *   12 = EXCLUSION;
     *   13 = HUE;
     *   14 = SATURATION;
     *   15 = COLOR;
     *   16 = LUMINOSITY;
     * @property BlendMode
     * @type number
     * @default null
     */
    BLEND_MODE: "BlendMode",
    EMIT: "Emit",
    // Tweens used in BaseElementParticle
    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",

    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },

    ROTATION_TWEEN: "RotationTween",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",

    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    /*
     * Style define animation. You can use one style on more than one place.
     * Style for ParticleElement has to be instance of ParticleStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    STYLE2: "Style2",
    START: "Start",
    STOPPING: "Stopping",
    STOP: "Stop",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/BaseElementParticle.ts
 **/